import React from "react";
import {
  AbsoluteFill,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useOnestFonts } from "./fonts";

/* ══════════════════════════════════════════════════════════════════
   Pangkasin — video promosi 1920x1080 @30fps, 540 frame (18 detik)
   6 <Sequence> × 90 frame (3 detik): hook → kasir → bonus → aman → AI → CTA
   ══════════════════════════════════════════════════════════════════ */

/* --- palet ------------------------------------------------------- */
const SLATE = "#020617"; // background dark slate
const CARD = "rgba(15, 23, 42, 0.72)";
const CARD_SOLID = "#0f172a";
const LINE = "rgba(148, 163, 184, 0.16)";
const TEXT = "#f8fafc";
const MUTED = "#94a3b8";
const GOLD = "#f59e0b"; // aksen barbershop
const EMERALD = "#10b981"; // fitur positif / WhatsApp
const ROSE = "#f43f5e"; // pain point / masalah

export const FPS = 30;
export const SCENE = 90; // durasi tiap sequence
export const TOTAL = SCENE * 6; // 540 frame = 18 detik

/** Titik mulai tiap scene (frame global di komposisi induk). */
export const T = {
  hook: 0, //  0s -  3s  masalah bos barbershop
  kasir: 90, //  3s -  6s  solusi Pangkasin + nota WA
  bonus: 180, //  6s -  9s  bonus otomatis + loyalty
  aman: 270, //  9s - 12s  anti-curang + laba bersih
  ai: 360, // 12s - 15s  AI WhatsApp omzet
  cta: 450, // 15s - 18s  multi-device + promo + CTA
};

const FONT = "Onest, 'Noto Color Emoji', 'DejaVu Sans', Arial, sans-serif";
const rp = (n: number) => "Rp " + Math.round(n).toLocaleString("id-ID");

/** hex + alpha → rgba() */
const a = (hex: string, alpha: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/* --- kerangka scene ---------------------------------------------- */
const Backdrop: React.FC<{ accent?: string }> = ({ accent = GOLD }) => (
  <>
    <AbsoluteFill
      style={{
        background: `radial-gradient(1100px 720px at 50% -6%, ${a(accent, 0.16)}, transparent 72%), ${SLATE}`,
      }}
    />
    <AbsoluteFill
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(148,163,184,0.20) 1px, transparent 1px)",
        backgroundSize: "34px 34px",
        opacity: 0.45,
      }}
    />
    <AbsoluteFill
      style={{
        background: `radial-gradient(1400px 900px at 50% 120%, ${a(accent, 0.10)}, transparent 70%)`,
      }}
    />
  </>
);

const Scene: React.FC<{
  children: React.ReactNode;
  accent?: string;
  style?: React.CSSProperties;
}> = ({ children, accent = GOLD, style }) => (
  <AbsoluteFill style={{ background: SLATE, fontFamily: FONT, color: TEXT, ...style }}>
    <Backdrop accent={accent} />
    {children}
  </AbsoluteFill>
);

/** Pembungkus animasi masuk berbasis spring() + interpolate(). */
const Pop: React.FC<{
  delay?: number;
  dir?: "up" | "down" | "left" | "right";
  distance?: number;
  zoom?: boolean;
  damping?: number;
  stiffness?: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({
  delay = 0,
  dir = "up",
  distance = 70,
  zoom = false,
  damping = 15,
  stiffness = 110,
  style,
  children,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame: frame - delay, fps, config: { damping, stiffness } });
  const d = interpolate(s, [0, 1], [distance, 0]);
  const t =
    dir === "up"
      ? `translateY(${d}px)`
      : dir === "down"
        ? `translateY(${-d}px)`
        : dir === "left"
          ? `translateX(${-d}px)`
          : `translateX(${d}px)`;
  return (
    <div
      style={{
        transform: zoom ? `translateY(${d}px) scale(${interpolate(s, [0, 1], [0.82, 1])})` : t,
        opacity: s,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** Judul kecil di atas konten scene. */
const Kicker: React.FC<{ text: string; color?: string; delay?: number }> = ({
  text,
  color = GOLD,
  delay = 0,
}) => (
  <Pop delay={delay} distance={30}>
    <div
      style={{
        display: "inline-block",
        padding: "10px 26px",
        borderRadius: 999,
        border: `2px solid ${a(color, 0.55)}`,
        background: a(color, 0.12),
        color,
        fontSize: 24,
        fontWeight: 800,
        letterSpacing: 4,
        textTransform: "uppercase",
      }}
    >
      {text}
    </div>
  </Pop>
);

const Card: React.FC<{
  children: React.ReactNode;
  accent?: string;
  style?: React.CSSProperties;
  padding?: number;
}> = ({ children, accent = GOLD, style, padding = 30 }) => (
  <div
    style={{
      background: CARD,
      border: `2px solid ${a(accent, 0.35)}`,
      borderRadius: 26,
      boxShadow: `0 30px 60px -30px ${a(accent, 0.45)}`,
      padding,
      boxSizing: "border-box",
      backdropFilter: "blur(2px)",
      ...style,
    }}
  >
    {children}
  </div>
);

/** Ikon bulat (centang / silang). */
const IconBullet: React.FC<{ glyph: string; accent: string; size?: number }> = ({
  glyph,
  accent,
  size = 62,
}) => (
  <div
    style={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: 18,
      background: a(accent, 0.16),
      border: `2px solid ${a(accent, 0.6)}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size * 0.5,
      lineHeight: 1,
    }}
  >
    {glyph}
  </div>
);

/** Angka berhitung naik. */
const Counter: React.FC<{
  to: number;
  delay?: number;
  durationInFrames?: number;
  format?: (n: number) => string;
  style?: React.CSSProperties;
}> = ({ to, delay = 0, durationInFrames = 40, format = rp, style }) => {
  const frame = useCurrentFrame();
  const v = interpolate(frame - delay, [0, durationInFrames], [0, to], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return <span style={{ fontVariantNumeric: "tabular-nums", ...style }}>{format(v)}</span>;
};

/* --- kursor ------------------------------------------------------ */
const Cursor: React.FC<{ x: number; y: number; scale?: number; opacity?: number }> = ({
  x,
  y,
  scale = 1,
  opacity = 1,
}) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      top: 0,
      transform: `translate(${x}px, ${y}px) scale(${scale})`,
      transformOrigin: "top left",
      opacity,
      zIndex: 60,
      filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.65))",
    }}
  >
    <svg width="40" height="50" viewBox="0 0 40 50">
      <path
        d="M4 2 L4 38 L14 28 L21 46 L29 42.5 L22 25.5 L35 25.5 Z"
        fill="#ffffff"
        stroke={SLATE}
        strokeWidth="3"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/** Efek gelombang klik di titik kursor. */
const ClickRipple: React.FC<{ x: number; y: number; start?: number; accent?: string }> = ({
  x,
  y,
  start = 0,
  accent = EMERALD,
}) => {
  const frame = useCurrentFrame();
  const p = interpolate(frame - start, [0, 18], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  if (p <= 0 || p >= 1) return null;
  const size = interpolate(p, [0, 1], [30, 190]);
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        borderRadius: "50%",
        border: `4px solid ${accent}`,
        opacity: interpolate(p, [0, 0.2, 1], [0, 0.9, 0]),
        zIndex: 55,
      }}
    />
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 1 — frame 0-89 | 0s-3s : HOOK & MASALAH BOS BARBERSHOP
   ══════════════════════════════════════════════════════════════════ */
const PROBLEMS = [
  { tag: "Selisih kas", t: "Uang di laci nggak cocok sama catatan" },
  { tag: "Komisi", t: "Bingung hitung bonus tiap barber di akhir bulan" },
  { tag: "Retensi", t: "Pelanggan setia nggak pernah dapat reward, jadi gampang pindah" },
  { tag: "Laba", t: "Nggak tahu sebenarnya untung atau cuma ramai" },
];

const ProblemCard: React.FC<{ text: string; tag: string; delay: number; index: number }> = ({
  text,
  tag,
  delay,
  index,
}) => (
  <Pop delay={delay} dir={index % 2 === 0 ? "left" : "right"} distance={80} damping={13}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 24,
        background: `linear-gradient(100deg, ${a(ROSE, 0.14)}, ${CARD})`,
        border: `2px solid ${a(ROSE, 0.42)}`,
        borderLeft: `8px solid ${ROSE}`,
        borderRadius: 26,
        padding: "32px 36px",
        boxShadow: `0 26px 50px -34px ${a(ROSE, 0.9)}`,
      }}
    >
      <IconBullet glyph="❌" accent={ROSE} size={76} />
      <div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: ROSE,
            marginBottom: 8,
          }}
        >
          {tag}
        </div>
        <div style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.3, color: TEXT }}>{text}</div>
      </div>
    </div>
  </Pop>
);

const Scene1Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame, fps, config: { damping: 14, stiffness: 92 } });
  const y = interpolate(s, [0, 1], [46, 0]);
  const glow = interpolate(frame, [0, 24, 88], [0, 1, 0.85], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <Scene accent={ROSE}>
      <AbsoluteFill
        style={{
          padding: "62px 120px 70px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ textAlign: "center", transform: `translateY(${y}px)`, opacity: s }}>
          <Kicker text="Masalah klasik barbershop" color={ROSE} />
          <div
            style={{
              fontSize: 62,
              fontWeight: 800,
              lineHeight: 1.18,
              marginTop: 22,
              letterSpacing: -1.2,
              textShadow: `0 0 ${40 * glow}px ${a(ROSE, 0.35)}`,
            }}
          >
            <span style={{ fontSize: 62 }}>✂️</span>{" "}Bos Barbershop, masih nyatat pemasukan di
            buku? <span style={{ color: ROSE }}>Kenal masalah ini?</span>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 34,
            marginTop: 44,
            flex: 1,
            alignContent: "center",
          }}
        >
          {PROBLEMS.map((p, i) => (
            <ProblemCard key={p.tag} text={p.t} tag={p.tag} delay={18 + i * 12} index={i} />
          ))}
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 2 — frame 90-179 | 3s-6s : SOLUSI PANGKASIN + KASIR CEPAT + NOTA WA
   ══════════════════════════════════════════════════════════════════ */
const KASIR_ROW = [
  { name: "Potong Rambut", barber: "Bang Raka", price: 35000 },
  { name: "Potong + Keramas", barber: "Bang Raka", price: 50000 },
  { name: "Cukur Jenggot", barber: "Adi", price: 20000 },
];

const KasirRow: React.FC<{
  name: string;
  barber: string;
  price: number;
  index: number;
}> = ({ name, barber, price, index }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 34 + index * 8;
  const s = spring({ frame: frame - at, fps, config: { damping: 16, stiffness: 120 } });
  const tick = interpolate(frame, [at + 12, at + 24], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        background: a("#1e293b", 0.85),
        border: `1px solid ${LINE}`,
        borderRadius: 16,
        padding: "16px 20px",
        marginBottom: 12,
        opacity: s,
        transform: `translateX(${interpolate(s, [0, 1], [-40, 0])}px)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: a(EMERALD, 0.18),
            border: `1.5px solid ${a(EMERALD, 0.6)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            transform: `scale(${tick})`,
          }}
        >
          ✅
        </div>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{name}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: MUTED }}>Barber: {barber}</div>
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: GOLD }}>{rp(price)}</div>
    </div>
  );
};

const Scene2Kasir: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // zoom-in mockup dashboard
  const zoom = spring({ frame: frame - 8, fps, config: { damping: 18, stiffness: 70 } });
  const scale = interpolate(zoom, [0, 1], [0.8, 1]);
  const blur = interpolate(zoom, [0, 0.7, 1], [8, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // kursor: jalan ke tombol → klik → ripple
  // tombol "Kirim Nota WhatsApp" berada di titik (620, 824) pada canvas 1920x1080
  const travel = spring({ frame: frame - 36, fps, config: { damping: 19, stiffness: 62 } });
  const cx = interpolate(travel, [0, 1], [1520, 620]);
  const cy = interpolate(travel, [0, 1], [1010, 824]);
  const press = interpolate(frame, [64, 68, 74], [1, 0.84, 0.84], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const sent = frame >= 74;

  const features = [
    {
      t: "Kasir cepat",
      d: "Pilih layanan, pilih barber, bayar pakai Cash, QRIS, atau Transfer.",
    },
    {
      t: "Nota digital lewat WhatsApp",
      d: "Lengkap dengan logo dan nama barbershop Anda.",
    },
  ];
  const paymentTotal = KASIR_ROW.reduce((acc, r) => acc + r.price, 0);
  const payActive = (i: number) =>
    interpolate(frame, [38 + i * 5, 46 + i * 5], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });

  return (
    <Scene accent={GOLD}>
      <AbsoluteFill style={{ padding: "52px 120px", boxSizing: "border-box" }}>
        {/* heading */}
        <div>
          <Pop delay={0} distance={40}>
            <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
              <div
                style={{
                  width: 74,
                  height: 74,
                  borderRadius: 20,
                  background: `linear-gradient(135deg, ${GOLD}, #fbbf24)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 40,
                  boxShadow: `0 20px 40px -18px ${a(GOLD, 0.9)}`,
                }}
              >
                💈
              </div>
              <div>
                <div style={{ fontSize: 52, fontWeight: 800, lineHeight: 1.05 }}>
                  <span style={{ color: GOLD }}>Pangkasin</span> — Aplikasi Kasir Khusus Barbershop
                </div>
                <div style={{ fontSize: 26, fontWeight: 600, color: MUTED, marginTop: 6 }}>
                  Satu aplikasi untuk kasir, barber, nota WhatsApp, sampai laporan.
                </div>
              </div>
            </div>
          </Pop>
        </div>

        {/* isi */}
        <div style={{ display: "flex", gap: 36, marginTop: 40 }}>
          {/* mockup dashboard */}
          <div
            style={{
              width: 1000,
              height: 700,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              opacity: interpolate(zoom, [0, 1], [0, 1]),
              filter: `blur(${blur}px)`,
              background: CARD_SOLID,
              border: `2px solid ${a(GOLD, 0.32)}`,
              borderRadius: 26,
              boxShadow: `0 40px 80px -40px ${a(GOLD, 0.55)}`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* title bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "16px 22px",
                background: a("#1e293b", 0.9),
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              <div style={{ width: 12, height: 12, borderRadius: 99, background: ROSE }} />
              <div style={{ width: 12, height: 12, borderRadius: 99, background: GOLD }} />
              <div style={{ width: 12, height: 12, borderRadius: 99, background: EMERALD }} />
              <div style={{ marginLeft: 16, fontSize: 20, fontWeight: 700, color: MUTED }}>
                pangkasin.app/kasir — Meja 2 · Rizky
              </div>
              <div style={{ marginLeft: "auto", fontSize: 20, fontWeight: 800, color: EMERALD }}>
                ● Online
              </div>
            </div>

            <div style={{ padding: 24, display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: MUTED,
                  textTransform: "uppercase",
                  letterSpacing: 3,
                  marginBottom: 14,
                }}
              >
                Layanan dipilih
              </div>

              {KASIR_ROW.map((r, i) => (
                <KasirRow key={r.name} {...r} index={i} />
              ))}

              {/* total + metode bayar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 6,
                  paddingTop: 16,
                  borderTop: `2px dashed ${LINE}`,
                }}
              >
                <div style={{ display: "flex", gap: 12 }}>
                  {["Cash", "QRIS", "Transfer"].map((m, i) => (
                    <div
                      key={m}
                      style={{
                        padding: "10px 22px",
                        borderRadius: 12,
                        fontSize: 22,
                        fontWeight: 800,
                        border: `2px solid ${a(i === 1 ? GOLD : LINE, i === 1 ? 0.75 : 1)}`,
                        background: i === 1 ? a(GOLD, 0.15) : "transparent",
                        color: i === 1 ? GOLD : MUTED,
                        transform: `scale(${interpolate(payActive(i), [0, 1], [0.7, 1])})`,
                        opacity: payActive(i),
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: MUTED }}>TOTAL</div>
                  <div style={{ fontSize: 46, fontWeight: 800, color: GOLD }}>
                    <Counter to={paymentTotal} delay={38} durationInFrames={28} />
                  </div>
                </div>
              </div>

              {/* tombol kirim nota — target klik kursor di (620, 884) */}
              <div
                style={{
                  marginTop: "auto",
                  height: 86,
                  borderRadius: 18,
                  background: sent
                    ? `linear-gradient(90deg, ${EMERALD}, #34d399)`
                    : `linear-gradient(90deg, #059669, ${EMERALD})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#022c22",
                  transform: `scale(${press})`,
                  boxShadow: `0 22px 44px -22px ${a(EMERALD, 0.95)}`,
                }}
              >
                <span style={{ fontSize: 30 }}>📲</span>
                {sent ? "Nota terkirim ✓" : "Kirim Nota WhatsApp"}
              </div>
            </div>
          </div>

          {/* kolom fitur */}
          <div style={{ display: "flex", flexDirection: "column", gap: 22, width: 644 }}>
            {features.map((f, i) => (
              <Pop key={f.t} delay={44 + i * 10} dir="right" distance={90} damping={14}>
                <Card
                  accent={EMERALD}
                  style={{ display: "flex", gap: 22, alignItems: "flex-start" }}
                >
                  <IconBullet glyph="✅" accent={EMERALD} size={64} />
                  <div>
                    <div style={{ fontSize: 34, fontWeight: 800, color: TEXT }}>{f.t}</div>
                    <div
                      style={{
                        fontSize: 25,
                        fontWeight: 600,
                        color: MUTED,
                        lineHeight: 1.4,
                        marginTop: 8,
                      }}
                    >
                      {f.d}
                    </div>
                  </div>
                </Card>
              </Pop>
            ))}

            <Pop delay={66} dir="right" distance={90}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "18px 24px",
                  borderRadius: 20,
                  background: a(EMERALD, 0.14),
                  border: `2px solid ${a(EMERALD, 0.5)}`,
                  fontSize: 26,
                  fontWeight: 700,
                  color: EMERALD,
                  opacity: sent ? 1 : 0.75,
                }}
              >
                {sent ? "✓ Nota terkirim ke WhatsApp Andi" : "Menunggu nota dikirim…"}
              </div>
            </Pop>
          </div>
        </div>
      </AbsoluteFill>

      {/* kursor & ripple */}
      <Cursor x={cx} y={cy} scale={press} />
      <ClickRipple x={620} y={824} start={66} accent={EMERALD} />
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 3 — frame 180-269 | 6s-9s : BONUS BARBER OTOMATIS + LOYALTY
   ══════════════════════════════════════════════════════════════════ */
const KOMISI = [
  { layanan: "Potong Rambut", txn: 62, komisi: 620000 },
  { layanan: "Potong + Keramas", txn: 41, komisi: 615000 },
  { layanan: "Cukur Jenggot & Kumis", txn: 28, komisi: 280000 },
  { layanan: "Creambath & Treatment", txn: 12, komisi: 360000 },
];

const KomisiRow: React.FC<{
  layanan: string;
  txn: number;
  komisi: number;
  index: number;
}> = ({ layanan, txn, komisi, index }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 12 + index * 7;
  const s = spring({ frame: frame - at, fps, config: { damping: 16, stiffness: 120 } });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "11px 0",
        borderBottom: `1px solid ${LINE}`,
        opacity: s,
        transform: `translateX(${interpolate(s, [0, 1], [-32, 0])}px)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ fontSize: 22, color: EMERALD, fontWeight: 800 }}>✅</div>
        <div style={{ fontSize: 26, fontWeight: 700 }}>{layanan}</div>
        <div style={{ fontSize: 20, fontWeight: 600, color: MUTED }}>{txn}× layanan</div>
      </div>
      <div style={{ fontSize: 27, fontWeight: 800, color: GOLD }}>{rp(komisi)}</div>
    </div>
  );
};

const LoyaltyStamp: React.FC<{ index: number }> = ({ index }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 16 + index * 4;
  const s = spring({ frame: frame - at, fps, config: { damping: 11, stiffness: 150 } });
  const filled = frame - at >= 0;
  return (
    <div
      style={{
        width: 92,
        height: 92,
        borderRadius: 20,
        border: `2.5px solid ${filled ? a(GOLD, 0.85) : LINE}`,
        background: filled ? a(GOLD, 0.16) : "rgba(148,163,184,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 40,
        transform: `scale(${interpolate(s, [0, 1], [0.4, 1])})`,
        opacity: filled ? 1 : 0.55,
        boxShadow: filled ? `0 14px 28px -16px ${a(GOLD, 1)}` : "none",
      }}
    >
      {filled ? "💈" : ""}
    </div>
  );
};

const Scene3Bonus: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const freeGranted = frame >= 56;
  const gift = spring({
    frame: frame - 56,
    fps,
    config: { damping: 9, stiffness: 130 },
  });

  return (
    <Scene accent={GOLD}>
      <AbsoluteFill
        style={{
          padding: "48px 120px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Kicker text="Otomatis, tanpa hitung manual" color={GOLD} />
        </div>

        <div style={{ display: "flex", gap: 22, marginTop: 30, alignItems: "stretch" }}>
          {/* kartu kiri — rekap komisi */}
          <Pop delay={8} dir="left" distance={90} damping={15} style={{ flex: 1 }}>
            <Card accent={GOLD} style={{ height: 700 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 8 }}>
                <IconBullet glyph="💰" accent={GOLD} size={64} />
                <div>
                  <div style={{ fontSize: 36, fontWeight: 800 }}>Rekap Komisi & Gaji Barber</div>
                  <div style={{ fontSize: 23, fontWeight: 600, color: MUTED }}>
                    Bonus dihitung otomatis per layanan yang dikerjakan
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                {KOMISI.map((k, i) => (
                  <KomisiRow key={k.layanan} {...k} index={i} />
                ))}
              </div>

              <div
                style={{
                  marginTop: 26,
                  padding: 24,
                  borderRadius: 20,
                  background: a(GOLD, 0.12),
                  border: `2px solid ${a(GOLD, 0.45)}`,
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: 3,
                    color: GOLD,
                    textTransform: "uppercase",
                  }}
                >
                  Total gaji + bonus bulan ini
                </div>
                <div style={{ fontSize: 74, fontWeight: 800, marginTop: 6, lineHeight: 1 }}>
                  <Counter to={3875000} delay={22} durationInFrames={44} />
                </div>
                <div style={{ fontSize: 23, fontWeight: 600, color: MUTED, marginTop: 10 }}>
                  Gaji pokok Rp 1.700.000 + komisi Rp 1.875.000 + bonus target Rp 300.000
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginTop: 20,
                  fontSize: 25,
                  fontWeight: 700,
                  color: EMERALD,
                }}
              >
                <span>✅</span> Sudah termasuk rekap gaji + bonus tiap bulan, siap dibayarkan
              </div>
            </Card>
          </Pop>

          {/* kartu kanan — loyalty stamp */}
          <Pop delay={16} dir="right" distance={90} damping={15} style={{ width: 690 }}>
            <Card accent={EMERALD} style={{ height: 700 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                <IconBullet glyph="🎟️" accent={EMERALD} size={64} />
                <div>
                  <div style={{ fontSize: 36, fontWeight: 800 }}>Program Loyalty</div>
                  <div style={{ fontSize: 23, fontWeight: 600, color: MUTED }}>
                    Potong 10×, Gratis 1×
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 30,
                  padding: 22,
                  borderRadius: 20,
                  background: "rgba(148,163,184,0.07)",
                  border: `1px solid ${LINE}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 22,
                    fontWeight: 700,
                    color: MUTED,
                    marginBottom: 16,
                  }}
                >
                  <span>Kartu stempel — Andi Saputra</span>
                  <span style={{ color: GOLD }}>{Math.min(10, Math.max(0, Math.floor((frame - 12) / 4)))}/10</span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 14,
                    justifyItems: "center",
                  }}
                >
                  {Array.from({ length: 10 }).map((_, i) => (
                    <LoyaltyStamp key={i} index={i} />
                  ))}
                </div>
              </div>

              <div
                style={{
                  marginTop: 26,
                  padding: "22px 24px",
                  borderRadius: 20,
                  background: freeGranted ? a(EMERALD, 0.16) : "rgba(148,163,184,0.07)",
                  border: `2px solid ${freeGranted ? a(EMERALD, 0.6) : LINE}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  transform: `scale(${freeGranted ? interpolate(gift, [0, 1], [0.9, 1]) : 1})`,
                }}
              >
                <IconBullet glyph={freeGranted ? "🎉" : "⏳"} accent={freeGranted ? EMERALD : MUTED} size={58} />
                <div>
                  <div
                    style={{
                      fontSize: 30,
                      fontWeight: 800,
                      color: freeGranted ? EMERALD : MUTED,
                    }}
                  >
                    {freeGranted ? "GRATIS 1× Potong Rambut!" : "Menghitung stempel…"}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: MUTED, marginTop: 6 }}>
                    Sistem yang menghitung otomatis, nggak perlu kartu kertas
                  </div>
                </div>
              </div>
            </Card>
          </Pop>
        </div>

        <div style={{ textAlign: "center", marginTop: 22 }}>
          <Pop delay={40} distance={26}>
            <div style={{ fontSize: 26, fontWeight: 700, color: EMERALD }}>
              ✅ Barber dapat komisi transparan, pelanggan balik lagi karena termotivasi reward
            </div>
          </Pop>
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 4 — frame 270-359 | 9s-12s : ANTI-CURANG & LABA BERSIH
   ══════════════════════════════════════════════════════════════════ */
const HARI = ["S", "S", "R", "K", "J", "S", "M"];
const BAR = [0.52, 0.61, 0.44, 0.7, 0.58, 0.95, 0.78];

const Scene4Aman: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const beam = spring({ frame: frame - 30, fps, config: { damping: 200, stiffness: 30 } });

  const metrics = [
    { k: "Jam paling ramai", v: "19.00 – 21.00" },
    { k: "Layanan terlaris", v: "Potong + Keramas" },
    { k: "Barber terlaris", v: "Bang Raka · 148 txn" },
  ];

  return (
    <Scene accent={GOLD}>
      <AbsoluteFill
        style={{
          padding: "48px 120px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        {/* banner keamanan */}
        <Pop delay={0} dir="down" distance={60}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 26,
              padding: "22px 32px",
              borderRadius: 24,
              background: `linear-gradient(90deg, ${a(GOLD, 0.18)}, ${a(EMERALD, 0.12)})`,
              border: `2px solid ${a(GOLD, 0.55)}`,
              boxShadow: `0 30px 60px -40px ${a(GOLD, 0.9)}`,
            }}
          >
            <IconBullet glyph="🔒" accent={GOLD} size={72} />
            <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.2 }}>
              ✅ Transaksi tidak bisa diedit atau dihapus karyawan, jadi uang aman 🔒
            </div>
            <div
              style={{
                marginLeft: "auto",
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: 3,
                color: GOLD,
                whiteSpace: "nowrap",
              }}
            >
              AUDIT LOG AKTIF
            </div>
          </div>
        </Pop>

        <div style={{ display: "flex", gap: 22, marginTop: 26, alignItems: "stretch" }}>
          {/* dashboard omzet */}
          <Pop delay={10} dir="left" distance={80} style={{ width: 1040 }}>
            <Card accent={GOLD} style={{ height: 620 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <IconBullet glyph="📊" accent={GOLD} size={56} />
                <div style={{ fontSize: 32, fontWeight: 800 }}>
                  ✅ Dashboard omzet — grafik harian
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 22,
                  height: 280,
                  marginTop: 26,
                }}
              >
                {BAR.map((b, i) => {
                  const h = interpolate(frame, [26 + i * 4, 52 + i * 4], [0, 1], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  });
                  return (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: 230 * b * h,
                          borderRadius: 14,
                          background:
                            i === 5
                              ? `linear-gradient(180deg, ${GOLD}, #b45309)`
                              : `linear-gradient(180deg, ${a(EMERALD, 0.85)}, ${a(EMERALD, 0.25)})`,
                          border: `1px solid ${a(i === 5 ? GOLD : EMERALD, 0.5)}`,
                          boxShadow: `0 20px 40px -26px ${a(i === 5 ? GOLD : EMERALD, 1)}`,
                        }}
                      />
                      <div style={{ fontSize: 22, fontWeight: 800, color: MUTED }}>{HARI[i]}</div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginTop: "auto",
                  paddingTop: 22,
                  borderTop: `1px solid ${LINE}`,
                }}
              >
                {metrics.map((m, i) => (
                  <Pop key={m.k} delay={40 + i * 8} distance={30} style={{ flex: 1 }}>
                    <div
                      style={{
                        padding: "16px 18px",
                        borderRadius: 16,
                        background: "rgba(148,163,184,0.08)",
                        border: `1px solid ${LINE}`,
                      }}
                    >
                      <div style={{ fontSize: 19, fontWeight: 700, color: MUTED }}>{m.k}</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: TEXT, marginTop: 6 }}>
                        {m.v}
                      </div>
                    </div>
                  </Pop>
                ))}
              </div>
            </Card>
          </Pop>

          {/* laporan laba bersih */}
          <Pop delay={18} dir="right" distance={80} style={{ flex: 1 }}>
            <Card accent={EMERALD} style={{ height: 620, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <IconBullet glyph="📈" accent={EMERALD} size={56} />
                <div style={{ fontSize: 32, fontWeight: 800 }}>✅ Laporan laba bersih</div>
              </div>

              <div style={{ marginTop: 26 }}>
                {[
                  { k: "Pemasukan bulan ini", v: 38600000, c: EMERALD },
                  { k: "Pengeluaran (gaji, sewa, stok)", v: 9750000, c: ROSE },
                ].map((r, i) => (
                  <div
                    key={r.k}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px 0",
                      borderBottom: `1px solid ${LINE}`,
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 700, color: MUTED }}>{r.k}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: r.c }}>
                      {i === 0 ? "+" : "−"} <Counter to={r.v} delay={16 + i * 8} durationInFrames={38} />
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 24,
                  padding: 26,
                  borderRadius: 22,
                  background: a(EMERALD, 0.14),
                  border: `2px solid ${a(EMERALD, 0.5)}`,
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: 3,
                    color: EMERALD,
                    textTransform: "uppercase",
                  }}
                >
                  Laba bersih
                </div>
                <div style={{ fontSize: 66, fontWeight: 800, lineHeight: 1.05, marginTop: 6 }}>
                  <Counter to={28850000} delay={30} durationInFrames={38} />
                </div>
                <div
                  style={{
                    height: 12,
                    borderRadius: 99,
                    background: "rgba(148,163,184,0.18)",
                    marginTop: 18,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${
                        interpolate(frame, [32, 62], [0, 74.7], {
                          extrapolateLeft: "clamp",
                          extrapolateRight: "clamp",
                        }) * beam
                      }%`,
                      background: `linear-gradient(90deg, ${EMERALD}, ${GOLD})`,
                    }}
                  />
                </div>
                <div style={{ fontSize: 21, fontWeight: 600, color: MUTED, marginTop: 10 }}>
                  Margin bersih 74,7% dari pemasukan
                </div>
              </div>

              <Pop delay={46} dir="up" distance={40} style={{ marginTop: "auto" }}>
                <div
                  style={{
                    height: 82,
                    borderRadius: 18,
                    background: `linear-gradient(90deg, ${EMERALD}, #34d399)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                    fontSize: 30,
                    fontWeight: 800,
                    color: "#022c22",
                    boxShadow: `0 22px 44px -24px ${a(EMERALD, 1)}`,
                  }}
                >
                  📄 Export ke Excel
                </div>
              </Pop>
            </Card>
          </Pop>
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 5 — frame 360-449 | 12s-15s : AI WHATSAPP OMZET
   (global: pertanyaan frame 370 → rel 10, balasan frame 395 → rel 35)
   ══════════════════════════════════════════════════════════════════ */
const TypingDots: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", height: 28 }}>
      {[0, 1, 2].map((i) => {
        const t = (frame - i * 5) % 18;
        const y = interpolate(t, [0, 9, 17], [0, -8, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        return (
          <div
            key={i}
            style={{
              width: 12,
              height: 12,
              borderRadius: 99,
              background: MUTED,
              transform: `translateY(${y}px)`,
            }}
          />
        );
      })}
    </div>
  );
};

const Scene5Ai: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // rel 10  = frame global 370 → pertanyaan owner
  const ask = spring({ frame: frame - 10, fps, config: { damping: 13, stiffness: 130 } });
  // rel 16-33 = indikator mengetik
  const typing = frame >= 16 && frame < 34;
  // rel 35  = frame global 395 → balasan AI
  const reply = spring({ frame: frame - 35, fps, config: { damping: 13, stiffness: 120 } });

  return (
    <Scene accent={EMERALD}>
      <AbsoluteFill
        style={{
          padding: "48px 120px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", gap: 46, alignItems: "center" }}>
          {/* mockup WhatsApp */}
          <div style={{ width: 760, flexShrink: 0 }}>
            <div
              style={{
                borderRadius: 40,
                overflow: "hidden",
                border: `2px solid ${a(EMERALD, 0.4)}`,
                background: "#0b141a",
                boxShadow: `0 50px 90px -50px ${a(EMERALD, 0.85)}`,
              }}
            >
              {/* header chat */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "18px 22px",
                  background: "#1f2c33",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 99,
                    background: `linear-gradient(135deg, ${EMERALD}, #34d399)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 30,
                  }}
                >
                  🤖
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800 }}>Pangkasin AI</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: EMERALD }}>
                    online · jawab dari data kasir
                  </div>
                </div>
                <div style={{ marginLeft: "auto", fontSize: 26, color: MUTED }}>⋮</div>
              </div>

              <div
                style={{
                  padding: 24,
                  minHeight: 570,
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                  backgroundImage:
                    "radial-gradient(circle, rgba(148,163,184,0.14) 1px, transparent 1px)",
                  backgroundSize: "26px 26px",
                }}
              >
                {/* pertanyaan owner (frame global 370) */}
                <div
                  style={{
                    alignSelf: "flex-end",
                    maxWidth: 520,
                    transform: `translateY(${interpolate(ask, [0, 1], [40, 0])}px) scale(${interpolate(ask, [0, 1], [0.85, 1])})`,
                    opacity: ask,
                    background: "#005c4b",
                    borderRadius: "20px 20px 6px 20px",
                    padding: "16px 22px",
                    boxShadow: "0 14px 30px -20px rgba(0,0,0,0.9)",
                  }}
                >
                  <div style={{ fontSize: 28, fontWeight: 700 }}>omzet hari ini berapa?</div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: "rgba(226,232,240,0.7)",
                      textAlign: "right",
                      marginTop: 6,
                    }}
                  >
                    19:04 ✓✓
                  </div>
                </div>

                {/* indikator mengetik */}
                {typing ? (
                  <div
                    style={{
                      alignSelf: "flex-start",
                      background: "#202c33",
                      borderRadius: "20px 20px 20px 6px",
                      padding: "16px 22px",
                    }}
                  >
                    <TypingDots />
                  </div>
                ) : null}

                {/* balasan AI (frame global 395) */}
                <div
                  style={{
                    alignSelf: "flex-start",
                    maxWidth: 640,
                    transform: `translateY(${interpolate(reply, [0, 1], [40, 0])}px) scale(${interpolate(reply, [0, 1], [0.85, 1])})`,
                    opacity: reply,
                    background: "#202c33",
                    borderRadius: "20px 20px 20px 6px",
                    padding: "20px 24px",
                    boxShadow: "0 14px 30px -20px rgba(0,0,0,0.9)",
                  }}
                >
                  <div style={{ fontSize: 27, fontWeight: 700, lineHeight: 1.35 }}>
                    🤖 Total Omzet Hari Ini: <span style={{ color: EMERALD }}>Rp 1.450.000</span>{" "}
                    (29 Pelanggan). Barber Terlaris: <span style={{ color: GOLD }}>Bang Raka</span>.
                    Laba Bersih: <span style={{ color: EMERALD }}>Rp 1.280.000</span>.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginTop: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    {["29 pelanggan", "Bang Raka · 11 txn", "Laba 88%"].map((c, i) => (
                      <div
                        key={c}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 10,
                          background: a(i === 1 ? GOLD : EMERALD, 0.16),
                          border: `1px solid ${a(i === 1 ? GOLD : EMERALD, 0.45)}`,
                          color: i === 1 ? GOLD : EMERALD,
                          fontSize: 19,
                          fontWeight: 700,
                        }}
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: "rgba(226,232,240,0.65)",
                      textAlign: "right",
                      marginTop: 10,
                    }}
                  >
                    19:04 ✓✓
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* penjelasan */}
          <div style={{ flex: 1, paddingTop: 24 }}>
            <Pop delay={6} distance={40}>
              <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1.14, letterSpacing: -1 }}>
                Tanya apa saja, <span style={{ color: EMERALD }}>dijawab dari data Anda</span>
              </div>
            </Pop>

            <Pop delay={20} dir="right" distance={70}>
              <Card accent={EMERALD} style={{ marginTop: 26 }}>
                <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                  <IconBullet glyph="✅" accent={EMERALD} size={60} />
                  <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.3 }}>
                    🤖 Tanya omzet lewat WhatsApp, AI langsung jawab dari data Anda!
                  </div>
                </div>
              </Card>
            </Pop>

            <div style={{ marginTop: 22, display: "grid", gap: 14 }}>
              {[
                "Omzet, laba, dan jumlah pelanggan hari ini",
                "Barber & layanan terlaris, jam paling ramai",
                "Stok tipis dan pengeluaran yang belum tercatat",
              ].map((t, i) => (
                <Pop key={t} delay={34 + i * 10} dir="right" distance={60}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      fontSize: 26,
                      fontWeight: 700,
                      color: TEXT,
                      padding: "14px 20px",
                      borderRadius: 16,
                      background: "rgba(148,163,184,0.08)",
                      border: `1px solid ${LINE}`,
                    }}
                  >
                    <span style={{ color: EMERALD }}>✓</span> {t}
                  </div>
                </Pop>
              ))}
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 6 — frame 450-539 | 15s-18s : MULTI-DEVICE, PROMO, CTA
   ══════════════════════════════════════════════════════════════════ */
const Scene6Cta: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const badge = spring({ frame, fps, config: { damping: 14, stiffness: 100 } });
  const head = spring({ frame: frame - 8, fps, config: { damping: 12, stiffness: 95 } });
  const pulse = 1 + Math.sin(frame / 7) * 0.028;
  const cta = spring({ frame: frame - 22, fps, config: { damping: 10, stiffness: 110 } });
  const ctaPulse = 1 + Math.sin(frame / 9) * 0.022;

  return (
    <Scene accent={GOLD}>
      <AbsoluteFill
        style={{
          padding: "56px 120px",
          boxSizing: "border-box",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        {/* badge multi-device */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            padding: "16px 34px",
            borderRadius: 999,
            border: `2px solid ${a(EMERALD, 0.5)}`,
            background: a(EMERALD, 0.12),
            fontSize: 30,
            fontWeight: 800,
            color: EMERALD,
            transform: `translateY(${interpolate(badge, [0, 1], [-40, 0])}px) scale(${interpolate(
              badge,
              [0, 1],
              [0.85, 1],
            )})`,
            opacity: badge,
          }}
        >
          <span style={{ fontSize: 32 }}>📱</span> Bisa dibuka dari HP atau laptop, tanpa install.
        </div>

        {/* headline promo */}
        <div
          style={{
            marginTop: 34,
            fontSize: 74,
            fontWeight: 800,
            lineHeight: 1.14,
            letterSpacing: -1.6,
            maxWidth: 1620,
            transform: `scale(${pulse})`,
            opacity: head,
          }}
        >
          <span style={{ fontSize: 74 }}>🎁</span>{"  "}
          <span
            style={{
              background: `linear-gradient(90deg, ${GOLD}, #fbbf24, ${EMERALD})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            GRATIS 2 BULAN
          </span>
          , tanpa syarat ribet. Coba dulu, rasakan bedanya, baru putuskan.
        </div>

        {/* CTA */}
        <div
          style={{
            marginTop: 40,
            transform: `scale(${ctaPulse})`,
            opacity: cta,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 20,
              padding: "30px 56px",
              borderRadius: 26,
              background: `linear-gradient(90deg, ${GOLD}, #fbbf24)`,
              color: "#1c1917",
              fontSize: 46,
              fontWeight: 800,
              letterSpacing: 0.5,
              boxShadow: `0 36px 70px -26px ${a(GOLD, 1)}`,
            }}
          >
            <span style={{ fontSize: 46 }}>📲</span> Daftar sekarang: 0895397073311
          </div>
        </div>

        <div
          style={{
            marginTop: 26,
            fontSize: 38,
            fontWeight: 800,
            color: EMERALD,
            letterSpacing: 1,
            opacity: interpolate(frame, [30, 44], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }),
          }}
        >
          barbershop.carroo.my.id
        </div>

        <div
          style={{
            marginTop: 18,
            fontSize: 26,
            fontWeight: 600,
            color: MUTED,
            opacity: interpolate(frame, [38, 52], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }),
          }}
        >
          Pangkasin — aplikasi kasir & manajemen barbershop 💈
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   KOMPOSISI — 6 sequence × 90 frame
   ══════════════════════════════════════════════════════════════════ */
export const PangkasinDarkPromo: React.FC = () => {
  useOnestFonts();
  return (
    <AbsoluteFill style={{ background: SLATE }}>
      <Sequence from={T.hook} durationInFrames={SCENE} name="1 · Hook & masalah">
        <Scene1Hook />
      </Sequence>
      <Sequence from={T.kasir} durationInFrames={SCENE} name="2 · Kasir cepat + nota WA">
        <Scene2Kasir />
      </Sequence>
      <Sequence from={T.bonus} durationInFrames={SCENE} name="3 · Bonus barber + loyalty">
        <Scene3Bonus />
      </Sequence>
      <Sequence from={T.aman} durationInFrames={SCENE} name="4 · Anti-curang + laba bersih">
        <Scene4Aman />
      </Sequence>
      <Sequence from={T.ai} durationInFrames={SCENE} name="5 · AI WhatsApp omzet">
        <Scene5Ai />
      </Sequence>
      <Sequence from={T.cta} durationInFrames={SCENE} name="6 · Multi-device + CTA">
        <Scene6Cta />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
